import React from 'react';
import { X, Trash2, ShoppingCart } from 'lucide-react';
import { useCart } from '../contexts/CartContext';
import { useNavigate } from 'react-router-dom';

const platformConfig = {
  instagram: {
    gradient: 'linear-gradient(135deg, #833ab4 0%, #fd1d1d 50%, #fcb045 100%)',
    icon: (
      <svg viewBox="0 0 24 24" className="w-5 h-5 text-white" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/>
        <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/>
        <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/>
      </svg>
    )
  },
  tiktok: {
    gradient: 'linear-gradient(135deg, #010101 0%, #69C9D0 50%, #EE1D52 100%)',
    icon: (
      <svg viewBox="0 0 24 24" className="w-5 h-5" fill="white">
        <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
      </svg>
    )
  },
  youtube: {
    gradient: 'linear-gradient(135deg, #FF0000 0%, #CC0000 100%)',
    icon: (
      <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none">
        <rect x="2" y="5" width="20" height="14" rx="3" fill="white"/>
        <path d="M10 8.5l7 3.5-7 3.5V8.5z" fill="#FF0000"/>
      </svg>
    )
  },
  facebook: {
    gradient: 'linear-gradient(135deg, #1877F2 0%, #0C5FD4 100%)',
    icon: (
      <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none">
        <rect width="24" height="24" rx="5" fill="#1877F2"/>
        <path d="M13.5 21v-7.5h2.25l.375-3H13.5V8.625c0-.75.375-1.125 1.125-1.125H16.5V5.25S15.375 5 14.25 5C11.625 5 10.5 6.375 10.5 8.625V10.5H8.25v3H10.5V21h3z" fill="white"/>
      </svg>
    )
  }
};

export default function CartModal({ isOpen, onClose }) {
  const { cartItems, removeFromCart, getCartTotal, clearCart } = useCart();
  const navigate = useNavigate();

  if (!isOpen) return null;

  const handleCheckout = () => {
    if (cartItems.length === 0) return;

    onClose();
    navigate('/checkout', {
      state: { cartItems }
    });
  };

  const getServiceName = (serviceType, platform) => {
    if (serviceType === 'followers') {
      return platform === 'youtube' ? 'Subscritores' : 'Seguidores';
    }
    const services = {
      likes: 'Likes',
      views: 'Visualizações'
    };
    return services[serviceType] || serviceType;
  };

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="absolute right-0 top-0 h-full w-full sm:w-96 bg-white shadow-xl transform transition-transform">
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <div className="flex items-center gap-2">
              <ShoppingCart className="w-5 h-5 text-pink-600" />
              <h2 className="text-lg font-bold text-gray-900">
                Carrinho ({cartItems.length})
              </h2>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full transition"
            >
              <X className="w-5 h-5 text-gray-600" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-4">
            {cartItems.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center">
                <ShoppingCart className="w-16 h-16 text-gray-300 mb-4" />
                <p className="text-gray-600 font-medium">Carrinho vazio</p>
                <p className="text-gray-400 text-sm mt-2">
                  Adicione itens para começar
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {cartItems.map((item) => {
                  const config = platformConfig[item.platform] || platformConfig.instagram;
                  return (
                    <div
                      key={item.id}
                      className="bg-gray-50 rounded-xl p-4 border border-gray-200"
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-3">
                          <img
                            src={item.profilePic}
                            alt={item.username}
                            className="w-12 h-12 rounded-full object-cover border-2 border-pink-200 flex-shrink-0"
                            onError={(e) => {
                              e.target.src = `https://ui-avatars.com/api/?name=${item.username}&size=200&background=9333ea&color=fff`;
                            }}
                          />
                          <div>
                            <p className="font-bold text-gray-900 text-sm">
                              @{item.displayName || item.username}
                            </p>
                            <p className="text-xs text-gray-600 capitalize">
                              {item.platform}
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={() => removeFromCart(item.id)}
                          className="p-1 hover:bg-red-100 rounded-full transition"
                        >
                          <Trash2 className="w-4 h-4 text-red-600" />
                        </button>
                      </div>

                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div>
                          <span className="text-gray-600">Serviço:</span>
                          <p className="font-semibold text-gray-900">
                            {getServiceName(item.serviceType, item.platform)}
                          </p>
                        </div>
                        <div>
                          <span className="text-gray-600">Quantidade:</span>
                          <p className="font-semibold text-gray-900">
                            {item.quantity.toLocaleString('fr-FR')}
                          </p>
                        </div>
                      </div>

                      <div className="mt-3 pt-3 border-t border-gray-200 flex justify-between items-center">
                        <span className="text-xs text-gray-600">Preço:</span>
                        <span className="text-lg font-bold bg-gradient-to-r from-pink-600 to-purple-600 bg-clip-text text-transparent">
                          €{parseFloat(item.price).toFixed(2)}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Footer */}
          {cartItems.length > 0 && (
            <div className="border-t border-gray-200 p-4 space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-gray-700 font-semibold">Total:</span>
                <span className="text-2xl font-bold bg-gradient-to-r from-pink-600 to-purple-600 bg-clip-text text-transparent">
                  €{getCartTotal().toFixed(2)}
                </span>
              </div>

              <button
                onClick={handleCheckout}
                className="w-full bg-gradient-to-r from-pink-600 to-purple-600 text-white py-3 rounded-xl font-bold hover:from-pink-700 hover:to-purple-700 transition-all shadow-lg"
              >
                Finalizar Compra →
              </button>

              <button
                onClick={clearCart}
                className="w-full bg-gray-100 text-gray-700 py-2 rounded-xl font-medium hover:bg-gray-200 transition text-sm"
              >
                Limpar Carrinho
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
